// src/app/components/ErrorBoundary.tsx
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportCrash } from '../monitoring/errorReporter';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level rendering safety net. Catches otherwise-uncaught errors
 * thrown during render anywhere below it in the tree and shows a
 * reload fallback instead of leaving the user with a blank page.
 *
 * Deliberately dumb: no retry-in-place, no error reporting wiring —
 * just "something broke, reload the app." Anything smarter belongs
 * to the component that actually failed, not this backstop.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    reportCrash(error, { componentStack: errorInfo.componentStack ?? undefined });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            direction: 'rtl',
          }}
        >
          <p style={{ fontSize: '1.125rem', fontWeight: 600 }}>خطایی رخ داد</p>
          <p style={{ opacity: 0.8 }}>لطفا دوباره تلاش کنید</p>
          <button type="button" onClick={this.handleReload}>
            بارگذاری مجدد
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
